import { ProgramSearchResultModel } from '../models/search-program-result.model';
import normalize from 'json-api-normalize';
import { SurveyHelper } from './survey.helper';
import { BehaviorSubject } from 'rxjs';
import { REGIONS_TO_STATES, SpecialtyModel } from '../models';

export class SearchServiceHelper {
  public static currentFilterData = new BehaviorSubject<any>({});

  public static parsePrograms(programsJson: any, fields?: string[], isDashboard?: boolean, aciSurveyResults?) {

    let programFields = [
      'id',
      'title',
      'drupal_internal__nid',
      'field_address',
      'field_expanded_listing',
      'field_specialty.title',
      'field_specialty.drupal_internal__nid',
      'field_specialty.id',
      'field_specialty.field_specialty_designation',
      'field_survey',
      'field_program_id',
      'path',
      'field_geolocation'
    ];

    if (fields && fields.length > 0) {
      programFields = programFields.concat(fields);
    }

    let normalizedData = programsJson;
    if (programsJson.jsonapi || (JSON.stringify(programsJson)).includes('jsonapi')) { // is json:api response
      normalizedData = normalize(programsJson).get(programFields);
    }
    // check to see if data is an array of programs or a single program
    const programResults = Array.isArray(normalizedData) ?
      normalizedData.map((p: any) => new ProgramSearchResultModel({
        uuid: p.id,
        nid: p.drupal_internal__nid,
        programName: p.title,
        programNumber: p.field_program_id,
        specialtyName: p.field_specialty ? p.field_specialty.title : '',
        isExpanded: p.field_expanded_listing,
        addressLine1: p.field_address ? p.field_address.address_line1 : '',
        addressLine2: p.field_address ? p.field_address.address_line2 : '',
        city: p.field_address ? p.field_address.locality : '',
        state: p.field_address ? p.field_address.administrative_area : '',
        country: p.field_address ? p.field_address.country_code : '',
        webSite: this.getWebSiteFromProgramSurvey(p.field_survey || []),
        programInfo: this.getProgramDetails(p.field_survey || [], p.field_expanded_listing, isDashboard),
        programInfoMobile: this.getProgramDetails(p.field_survey || [], p.field_expanded_listing, isDashboard, true),
        surveyData: this.getSurveyData(p.field_survey || []),
        specialty: new SpecialtyModel({
          uuid: p.field_specialty ? p.field_specialty.id : '',
          nid: p.field_specialty ? p.field_specialty.drupal_internal__nid : '',
          name: p.field_specialty ? p.field_specialty.title : '',
          type: p.field_specialty ? p.field_specialty.field_specialty_designation : ''
        }),
        workingCopyUrl: this.getWorkingCopyUrl(programsJson, p.field_program_id),
        cmsPath: p.path,
        geoLocation: p.field_geolocation,
        aci_score: this.getAciScore(p.field_program_id, aciSurveyResults),
        addedToCompare: false
      }))
      : [
        new ProgramSearchResultModel({
          uuid: normalizedData.id,
          nid: normalizedData.drupal_internal__nid,
          programName: normalizedData.title,
          programNumber: normalizedData.field_program_id,
          specialtyName: normalizedData.field_specialty ? normalizedData.field_specialty.title : '',
          isExpanded: normalizedData.field_expanded_listing,
          addressLine1: normalizedData.field_address ? normalizedData.field_address.address_line1 : '',
          addressLine2: normalizedData.field_address ? normalizedData.field_address.address_line2 : '',
          city: normalizedData.field_address ? normalizedData.field_address.locality : '',
          state: normalizedData.field_address ? normalizedData.field_address.administrative_area : '',
          country: normalizedData.field_address ? normalizedData.field_address.country_code : '',
          webSite: this.getWebSiteFromProgramSurvey(normalizedData.field_survey || []),
          programInfo: this.getProgramDetails(normalizedData.field_survey || [], normalizedData.field_expanded_listing, isDashboard),
          programInfoMobile: this.getProgramDetails(normalizedData.field_survey || [], normalizedData.field_expanded_listing, isDashboard, true),
          surveyData: this.getSurveyData(normalizedData.field_survey || []),
          specialty: new SpecialtyModel({
            uuid: normalizedData.field_specialty ? normalizedData.field_specialty.id : '',
            nid: normalizedData.field_specialty ? normalizedData.field_specialty.drupal_internal__nid : '',
            name: normalizedData.field_specialty ? normalizedData.field_specialty.title : '',
            type: normalizedData.field_specialty ? normalizedData.field_specialty.field_specialty_designation : ''
          }),
          workingCopyUrl: this.getWorkingCopyUrl(programsJson, normalizedData.field_program_id),
          cmsPath: normalizedData.path,
          geoLocation: normalizedData.field_geolocation,
          aci_score: this.getAciScore(normalizedData.field_program_id, aciSurveyResults),
          addedToCompare: false
        })
      ];
    return programResults;
  }

  private static getAciScore(field_program_id, aciSurveyResults) {
    let tempAciScore = '0';
    if (aciSurveyResults) {
      aciSurveyResults.map((aci) => {
        if (field_program_id === aci.program_number) {
          tempAciScore = aci.aci_score;
        }
      });
    }
    return tempAciScore;
  }

  private static getWebSiteFromProgramSurvey(surveys: any) {
    if (!surveys || !surveys.dataset) {
      return '';
    }

    // we need an array
    if (Array.isArray(surveys.dataset.data) && (surveys.dataset.data as []).length === 0) {
      return '';
    }

    const siteAddress = surveys.dataset.data[0].attributes.field_website || '';

    return siteAddress.length > 0 ? siteAddress : '';
  }

  private static getSurveyData(surveys: any) {
    if (Array.isArray(surveys) && (surveys as []).length === 0) {
      return [];
    }

    // find the most recent published surveys
    let survey: any = {};
    let mostRecentYear = 0;

    (surveys.dataset.data as any[])
      .filter((s: any) => s.attributes.moderation_state === 'published')
      .forEach((s: any) => {
        const year = +s.type.substring(s.type.length - 4);
        if (mostRecentYear < year) {
          mostRecentYear = year;
          survey = s;
        }
      });

    // get the data from the survey and put it into the returning array
    return Object.keys(survey).length > 0 ? SurveyHelper.getSurveyFieldList(survey) : [];
  }

  private static getProgramDetails(survey: any, isExpanded: boolean, dashboard: boolean, summary = false) {
    const surveyData = this.getSurveyData(survey);
    if (!dashboard) {
      return SurveyHelper.toSearchCardResultList(surveyData, isExpanded, dashboard);
    } else {
      // change false to summary
      const formattedSurvey = SurveyHelper.toDashboardCardResultList(surveyData, false);
      return formattedSurvey;
    }
  }

  private static getWorkingCopyUrl(json: any, programNumber: string) {
    if (json.data) {
      const program: any = (json.data as []).filter((p: any) => p.attributes.field_program_id === programNumber);
      return program.length === 1 && program[0].links['working-copy'] ? program[0].links['working-copy'].href : '';
    }

    return '';
  }

  public static filterProgramData(filters: any, programs: ProgramSearchResultModel[]) {
    const filteredPrograms = programs.filter(p => {
      // specialties
      if (filters.spec.length > 0 && p.specialty) {
        return (filters.spec.indexOf(p.specialty.nid.toString()) > -1);
      }

      return true;
    }).filter(p => {
      // total positions
      if (filters.tp.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const totalPositions = p.surveyData.find(s => s.field === 'field_total_positions') || { value: null };
        const programTotalPos = totalPositions.value ? (totalPositions.value as number) : 0;
        const min = filters.tp[0];
        const max = filters.tp[1];

        return programTotalPos >= min && programTotalPos <= max;
      }

      return true;
    }).filter(p => {
      // total first year positions
      if (filters.tfyp.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const pgmSizeField = p.surveyData.find(s => s.field === 'field_program_size') || { value: null };
        const programTotalFirstYearPos = pgmSizeField.value ? (pgmSizeField.value as number)[0] : 0;
        const min = filters.tfyp[0];
        const max = filters.tfyp[1];

        return programTotalFirstYearPos >= min && programTotalFirstYearPos <= max;
      }

      return true;
    }).filter(p => {
      // % usmd
      if (filters.usmd.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const usmdPctField = p.surveyData.find(s => s.field === 'field_pct_usmd') || { value: null };
        const min = filters.usmd[0];
        const max = filters.usmd[1];

        return usmdPctField.value ? +usmdPctField.value >= min && +usmdPctField.value <= max : true;
      }

      return true;
    }).filter(p => {
      // % img
      if (filters.img.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const imgPctField = p.surveyData.find(s => s.field === 'field_pct_img') || { value: null };
        const min = filters.img[0];
        const max = filters.img[1];

        return imgPctField.value ? +imgPctField.value >= min && +imgPctField.value <= max : true;
      }

      return true;
    }).filter(p => {
      // % do
      if (filters.do.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const doPctField = p.surveyData.find(s => s.field === 'field_pct_do') || { value: null };
        const min = filters.do[0];
        const max = filters.do[1];

        return doPctField.value ? +doPctField.value >= min && +doPctField.value <= max : true;
      }

      return true;
    }).filter(p => {
      // first year salary
      if (filters.fys.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const gradYearStatsField = p.surveyData.find(s => s.field === 'field_first_year_salary') || { value: [0] };
        const firstYearSalary = gradYearStatsField.value || 0;
        const min = filters.fys[0];
        const max = filters.fys[1];

        return firstYearSalary >= min && firstYearSalary <= max;
      }

      return true;
    }).filter(p => {
      // average hours per week
      if (filters.hrs.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const avgHoursWkField = p.surveyData.find(s => s.field === 'field_avg_hours_on_duty_y1') || { value: null };
        const min = filters.hrs[0];
        const max = filters.hrs[1];

        return avgHoursWkField.value ? avgHoursWkField.value >= min && avgHoursWkField.value <= max : false;
      }

      return true;
    }).filter(p => {
      // locations
      if (filters.loc.length > 0 && p.state) {

        REGIONS_TO_STATES.forEach(r => {
          if (filters.loc.includes(r.name)) {
            filters.loc = filters.loc.concat(r.states);
          }
        });

        return (filters.loc as []).some(l => p.state === l);
      }

      return true;
    }).filter(p => {
      // visa
      if (filters.visa.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const visaFilters = filters.visa as string[];
        const visaField = p.surveyData.find(s => s.field === 'field_visa_status') || { value: [] };

        return (visaField.value.indexOf('J-1') > -1 && visaFilters.indexOf('J-1') > -1)
          || (visaField.value.indexOf('H-1B') > -1 && visaFilters.indexOf('H-1B') > -1);
      }

      return true;
    }).filter(p => {
      // child care provided
      if (filters.cc.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const programOffersField = p.surveyData.find(s => s.field === 'field_program_offers') || { value: [] };

        return programOffersField.value ? (programOffersField.value as string[]).some(po => po.indexOf('child care') > -1) : false;
      }

      return true;
    }).filter(p => {
      // san francisco match
      if (filters.at.length > 0 && filters.at.indexOf('San Francisco') > -1 && p.surveyData && p.surveyData.length > 0) {
        const sanFranciscoField = p.surveyData.find(s => s.field === 'field_participates_sf_match') || { value: false };

        return sanFranciscoField.value || false;
      }

      return true;
    }).filter(p => {
      // nrmp match
      if (filters.at.length > 0 && filters.at.some(f => f.indexOf('NRMP Match') > -1) && p.surveyData && p.surveyData.length > 0) {
        const applicationTypeFilters = (filters.at as string[]).map(f => f.toLowerCase());
        const nrmpMatchField = p.surveyData.find(s => s.field === 'field_match_nrmp_codes') || { value: [] };
        const hasNrmpMatch = nrmpMatchField.value ? (nrmpMatchField.value as []).length > 0 : false;

        return applicationTypeFilters.some(filt => filt.indexOf('participates in nrmp') > -1) ? hasNrmpMatch : false
          || applicationTypeFilters.some(filt => filt.indexOf('does not participate in nrmp') > -1) ? !hasNrmpMatch : false;
      }

      return true;
    }).filter(p => {
      // eras
      if (filters.at.length > 0 && filters.at.some(f => f.indexOf('ERAS') > -1) && p.surveyData && p.surveyData.length > 0) {
        const applicationTypeFilters = (filters.at as string[]).map(f => f.toLowerCase());
        const erasField = p.surveyData.find(s => s.field === 'field_participates_in_eras') || { value: false };

        return applicationTypeFilters.indexOf('participates in eras') > -1 ? (erasField.value as boolean) : false
          || applicationTypeFilters.indexOf('does not participate in eras') > -1 ? !(erasField.value as boolean) : false;
      }

      return true;
    }).filter(p => {
      // prelim positions
      if (filters.at.length > 0 && filters.at.indexOf('Offers Preliminary Positions') > -1 && p.surveyData && p.surveyData.length > 0) {
        const prelimPositionField = p.surveyData.find(s => s.field === 'field_offers_preliminary_positio') || { value: false };

        return prelimPositionField.value || false;
      }

      return true;
    }).filter(p => {
      // comlex required
      if (filters.at.length > 0 && filters.at.some(at => at.toLowerCase().indexOf('comlex') > -1) && p.surveyData && p.surveyData.length > 0) {
        const comlexLvlOneField = p.surveyData.find(s => s.field === 'field_comlex_level_1') || { value: '' };
        const comlexLvlTwoField = p.surveyData.find(s => s.field === 'field_comlex_level_2') || { value: '' };
        const comlexReqd = (comlexLvlOneField.value || '').toLowerCase() === 'yes' ? true : false
          || comlexLvlTwoField.value ? comlexLvlTwoField.value as boolean : false;

        return comlexReqd;
      }

      return true;
    }).filter(p => {
      // usmle required
      if (filters.at.length > 0 && filters.at.some(at => at.indexOf('USMLE') > -1) && p.surveyData && p.surveyData.length > 0) {
        const applicationTypeFilters = filters.at as string[];
        const usmleStepOneField = p.surveyData.find(s => s.field === 'field_usmle_step_1_required') || { value: false };
        const usmleStepTwoField = p.surveyData.find(s => s.field === 'field_usmle_step_2_required') || { value: false };
        const usmleStepThreeField = p.surveyData.find(s => s.field === 'field_usmle_step_3_required') || { value: false };

        const usmleReqd = usmleStepOneField.value ? usmleStepOneField.value as boolean : false
          || usmleStepTwoField.value ? usmleStepTwoField.value as boolean : false
            || usmleStepThreeField.value ? usmleStepThreeField.value as boolean : false;

        if (applicationTypeFilters.indexOf('Only USMLE') > -1) {
          const comlexLvlOneField = p.surveyData.find(s => s.field === 'field_comlex_level_1') || { value: '' };
          const comlexLvlTwoField = p.surveyData.find(s => s.field === 'field_comlex_level_2') || { value: '' };
          const comlexReqd = (comlexLvlOneField.value || '').toLowerCase() === 'yes' ? true : false
            || comlexLvlTwoField.value ? comlexLvlTwoField.value as boolean : false;

          return !comlexReqd && usmleReqd;
        }

        return usmleReqd;
      }

      return true;
    }).filter(p => {
      // accepting applications
      if (filters.at.length > 0 && filters.at.indexOf('Accepting Applications') > -1 && p.surveyData && p.surveyData.length > 0) {
        const acceptingCurrentYear = p.surveyData.find(s => s.field === 'field_accepting_current_year') || { value: false };
        const acceptingNextYear = p.surveyData.find(s => s.field === 'field_accepting_next_year') || { value: false };

        return acceptingCurrentYear.value ? acceptingCurrentYear.value as boolean : false
          || acceptingNextYear.value ? acceptingNextYear.value as boolean : false;
      }

      return true;
    }).filter(p => {
      // special tracks
      if (filters.st.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const specialTracksFilters = filters.st as string[];
        const specialTracksField = p.surveyData.find(s => s.field === 'field_special_tracks') || { value: [] };

        // return true when the filter selected by the user matches any value
        // in the array of special tracks from the survey
        return specialTracksField.value ? specialTracksFilters.some((filt: string) => {
          return (specialTracksField.value as [])
            .some((st: string) => st.toLowerCase().indexOf(filt.toLowerCase()) > -1);
        }) : false;
      }

      return true;
    }).filter(p => {
      // program type
      if (filters.pt.length > 0 && p.surveyData && p.surveyData.length > 0) {
        const programTypeFilters = filters.pt as string[];
        const programTypeField = p.surveyData.find(s => s.field === 'field_program_best_described_as') || { value: '' };
        const removeChars = (str: string) => {
          return str.replace(/\s+/g, '')
            .replace(/\/+/g, '')
            .replace(/\-+/g, '')
            .toLowerCase();
        };

        return programTypeField.value ? programTypeFilters.some(filt => removeChars(filt) === removeChars(programTypeField.value.toString())) : false;
      }

      return true;
    });

    return filteredPrograms;
  }
}
